import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Header from "@/components/Header";
import type { Database } from "@/integrations/supabase/types";
import placeholder from "@/assets/placeholder.png?url";


type AvailInsert = Database["public"]["Tables"]["availability"]["Insert"];


import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  X,
  MapPin,
  DollarSign,
  Settings,
  Camera,
  Zap,
  Shield,
  Lock,
  Eye,
  CalendarDays,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/SessionProvider";
import { useTermsAcceptance } from "@/hooks/useTermsAcceptance";
import TermsAcceptanceDialog from "@/components/TermsAcceptanceDialog";

// 🔹 NYT: availability-editor + type
import AvailabilityEditor, { DraftAvailability } from "@/components/AvailabilityEditor";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

// ====== SCHEMA ======
const formSchema = z.object({
  title: z.string().min(10, "Titel skal være mindst 10 tegn"),
  address: z.string().min(5, "Adresse er påkrævet"),
  description: z.string().min(20, "Beskrivelse skal være mindst 20 tegn"),
  rules: z.string().optional(),
  size: z.enum(["S", "M", "L", "XL"]),
  base_price_day: z.number().min(1, "Pris pr. dag er påkrævet"),
  base_price_hour: z.number().optional(),
  cancellation_policy: z.enum(["flex", "mod", "strict"]),
  facilities: z.object({
    charger: z.boolean(),
    charger_type: z.string().optional(),
    charger_power: z.string().optional(),
    covered: z.boolean(),
    gate: z.boolean(),
    cctv: z.boolean(),
  }),
  visibility: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

const STEPS = [
  { id: 1, title: "Grundlæggende info", icon: MapPin },
  { id: 2, title: "Priser", icon: DollarSign },
  { id: 3, title: "Faciliteter", icon: Settings },
  { id: 4, title: "Tilgængelighed", icon: CalendarDays },
  { id: 5, title: "Billeder", icon: Camera },
  { id: 6, title: "Gennemgå", icon: Eye },
];

const SIZE_LABELS = {
  S: "Lille (< 4.2 m)",
  M: "Medium (4.2–4.8 m)",
  L: "Stor (4.8–5.2 m)",
  XL: "Ekstra stor (> 5.2 m)",
} as const;


// ====== NYE HELPERS (availability → DB rows) ======
function toIso(date: string, time: string) {
  // Simple lokal ISO for MVP (uden TZ-justering)
  return new Date(`${date}T${time}:00`).toISOString();
}

function buildInserts(listingId: string, drafts: DraftAvailability[]): AvailInsert[] {
  return drafts.map((d): AvailInsert => {
    if (d.kind === "single") {
      const start = toIso(d.date, d.start);
      const end   = toIso(d.date, d.end);
      return {
        listing_id: listingId,
        type: "single",        // matcher enum-union
        start_at: start,
        end_at: end,
        rrule: null,
      };
    }
    if (d.kind === "range") {
      const start = toIso(d.startDate, d.startTime);
      const end   = toIso(d.endDate, d.endTime);
      return {
        listing_id: listingId,
        type: "range",
        start_at: start,
        end_at: end,
        rrule: null,
      };
    }
    // recurring
    const dtstart = toIso(d.fromDate, d.startTime);
    const untilUtc = new Date(`${d.untilDate}T23:59:59Z`)
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}Z$/, "Z");
    const rrule = `FREQ=WEEKLY;BYDAY=${d.days.join(",")};UNTIL=${untilUtc}`;
    const end = toIso(d.fromDate, d.endTime);
    return {
      listing_id: listingId,
      type: "recurring",
      start_at: dtstart,
      end_at: end,
      rrule,
    };
  });
}


// ====== FOTO-UPLOAD ======
async function uploadListingPhotos(listingId: string, userId: string, files: File[]): Promise<string[]> {
  const urls: string[] = [];
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    const ext = (f.name.split(".").pop() || "jpg").toLowerCase();
    const path = `${userId}/${listingId}/${Date.now()}_${i}.${ext}`;

    const { error: upErr } = await supabase.storage.from("listing-photos").upload(path, f, {
      cacheControl: "3600",
      upsert: false,
    });
    if (upErr) throw upErr;

    const { data } = supabase.storage.from("listing-photos").getPublicUrl(path);
    urls.push(data.publicUrl);
  }
  return urls;
}

export default function CreateListing() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading } = useSession();

  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  // 🔹 NYT: availability state
  const [availability, setAvailability] = useState<DraftAvailability[]>([]);
  // 🔹 Gem koordinater fra autocomplete
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<FormData | null>(null);
  const [sizeModalOpen, setSizeModalOpen] = useState(false);

  const { hasAcceptedHost, isLoading: termsLoading } = useTermsAcceptance();

  // Hvis ikke logget ind → send til login med redirect tilbage hertil
  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth?mode=signin&redirect=/create-listing");
    }
  }, [loading, user, navigate]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      address: "",
      description: "",
      rules: "",
      size: "M",
      base_price_day: 100,
      base_price_hour: 12,
      cancellation_policy: "mod",
      facilities: {
        charger: false,
        charger_type: "",
        charger_power: "",
        covered: false,
        gate: false,
        cctv: false,
      },
      visibility: true,
    },
    mode: "onBlur",
  });

  const progress = (currentStep / STEPS.length) * 100;

const nextStep = async () => {
  if (currentStep === 1) {
    const ok = await form.trigger(["title", "address", "description", "size"], { shouldFocus: true });
    if (!ok) return;
  }
  if (currentStep === 2) {
    const ok = await form.trigger(["base_price_day", "cancellation_policy"], { shouldFocus: true });
    if (!ok) return;
  }
  if (currentStep === 5 && uploadedImages.length < 3) return;

  if (currentStep < STEPS.length) setCurrentStep((s) => s + 1);
};

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (uploadedImages.length + files.length > 10) {
      toast({
        title: "For mange billeder",
        description: "Du kan maksimalt uploade 10 billeder",
        variant: "destructive",
      });
      return;
    }
    setUploadedImages((prev) => [...prev, ...files]);
  };

  const removeImage = (index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  // ====== MUTATION: create listing + upload fotos + availability ======
  const createListingMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!user) throw new Error("Du skal være logget ind for at oprette en plads.");
      if (uploadedImages.length < 3) throw new Error("Upload mindst 3 billeder.");

      // 0) Brug koordinater fra autocomplete hvis tilgængelige
      let geo_lat: number | null = coordinates?.lat ?? null;
      let geo_lng: number | null = coordinates?.lng ?? null;

      // 1) Insert listing (uden photos)
      const { data: inserted, error: insErr } = await supabase
        .from("listings")
        .insert({
          owner_id: user.id,
          title: data.title,
          address: data.address,
          size: data.size,
          facilities: data.facilities,
          rules: data.rules,
          description: data.description,
          base_price_day: Math.round((data.base_price_day ?? 0) * 100), // kr → øre
          base_price_hour: data.base_price_hour != null ? Math.round(data.base_price_hour * 100) : null,
          cancellation_policy: data.cancellation_policy,
          visibility: data.visibility,
          photos: [], // udfyldes efter upload
          geo_lat,
          geo_lng,
        })
        .select("id")
        .single();

      if (insErr) throw insErr;
      const listingId = inserted.id as string;

      // 2) Upload fotos → public URLs
      const photoUrls = await uploadListingPhotos(listingId, user.id, uploadedImages);

      // 3) Update listing med photos[]
      const { error: upErr } = await supabase.from("listings").update({ photos: photoUrls }).eq("id", listingId);
      if (upErr) throw upErr;

      // 4) 🔹 Insert availability hvis noget er valgt
      if (availability.length) {
        const rows = buildInserts(listingId, availability);
        const { error: avErr } = await supabase.from("availability").insert(rows);
        if (avErr) throw avErr;
      }

      return listingId;
    },
    onSuccess: async (listingId) => {
      // Tjek om udlejer kan modtage udbetaling - ellers er pladsen skjult
      let payoutsReady = false;
      if (user) {
        const { data: profile } = await supabase
          .from("users")
          .select("payouts_enabled")
          .eq("id", user.id)
          .maybeSingle();
        payoutsReady = !!profile?.payouts_enabled;

        if (!payoutsReady) {
          supabase.functions
            .invoke("send-stripe-setup-reminder", {
              body: { userId: user.id, listingId },
            })
            .catch((e) => console.error("Kunne ikke sende Stripe-påmindelse:", e));
        }
      }

      toast({
        title: "Plads oprettet!",
        description: payoutsReady
          ? "Din parkeringsplads er nu tilgængelig for lejere."
          : "Din plads bliver synlig, så snart din udbetalingsopsætning er færdig. Vi har sendt dig en mail med link.",
      });
      navigate(payoutsReady ? `/spot/${listingId}` : "/dashboard");
    },
    onError: (e: any) => {
      toast({
        title: "Fejl",
        description: e?.message ?? "Der opstod en fejl ved oprettelse af din plads.",
        variant: "destructive",
      });
    },
  });

const onSubmit = async (data: FormData) => {
  // 🚧 Hvis ikke sidste step → gå bare videre til næste step og stop!
  if (currentStep < STEPS.length) {
    await nextStep();
    return;
  }

  // ✅ Først her er vi på sidste trin (Gennemgå) → lav de normale guards + opret
  if (!user) {
    navigate("/auth?mode=signin&redirect=/create-listing");
    return;
  }
  
  // Tjek om host har accepteret vilkår
  if (!hasAcceptedHost) {
    setPendingSubmitData(data); // Gem data til efter accept
    setShowTermsDialog(true);
    return;
  }
  
  if (uploadedImages.length < 3) {
    toast({
      title: "Mangler billeder",
      description: "Du skal uploade mindst 3 billeder",
      variant: "destructive",
    });
    return;
  }
  await createListingMutation.mutateAsync(data);
};


  // ====== UI ======
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-4">Grundlæggende information</h2>
              <p className="text-muted-foreground mb-6">Fortæl os om din parkeringsplads og hvor den er placeret.</p>
            </div>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Titel *</FormLabel>
                  <FormControl>
                    <Input placeholder="f.eks. Privat p-plads ved Østerbro St." {...field} required />
                  </FormControl>
                  <FormDescription>Skriv en beskrivende titel</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Adresse *</FormLabel>
                  <FormControl>
                    <AddressAutocomplete
                      value={field.value}
                      onChange={(value, coords) => {
                        field.onChange(value);
                        if (coords) {
                          setCoordinates(coords);
                        }
                      }}
                      placeholder="Søg efter adresse..."
                      required
                    />
                  </FormControl>
                  <FormDescription>Vælg fra foreslåede adresser for bedst visning</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plads størrelse *</FormLabel>
                  <FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSizeModalOpen(true)}
                      className="w-full justify-between"
                    >
                      <span>{field.value ? SIZE_LABELS[field.value] : "Vælg størrelse"}</span>
                      <Settings className="h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                  <FormMessage />
                  
                  <Dialog open={sizeModalOpen} onOpenChange={setSizeModalOpen}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Vælg plads størrelse</DialogTitle>
                        <DialogDescription>
                          Vælg den størrelse der passer til din parkeringsplads
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="space-y-3 py-4">
                        {[
                          { value: "S" as const, label: "Lille (< 4.2 m)", desc: "Smart, Fiat 500" },
                          { value: "M" as const, label: "Medium (4.2–4.8 m)", desc: "VW Golf, Toyota Corolla" },
                          { value: "L" as const, label: "Stor (4.8–5.2 m)", desc: "BMW 5-serie, Volvo XC90" },
                          { value: "XL" as const, label: "Ekstra stor (> 5.2 m)", desc: "Varevogn, stor SUV" },
                        ].map(({ value, label, desc }) => (
                          <Button
                            key={value}
                            type="button"
                            variant={field.value === value ? "default" : "outline"}
                            className="w-full justify-start h-auto py-4"
                            onClick={() => {
                              field.onChange(value);
                              setSizeModalOpen(false);
                            }}
                          >
                            <div className="text-left">
                              <div className="font-semibold">{label}</div>
                              <div className="text-xs opacity-80">{desc}</div>
                            </div>
                          </Button>
                        ))}
                      </div>
                    </DialogContent>
                  </Dialog>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Beskrivelse *</FormLabel>
                  <FormControl>
                    <Textarea rows={4} placeholder="Beskriv pladsen og adgang" {...field} required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rules"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Regler (valgfrit)</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder="Evt. begrænsninger…" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-4">Prissætning</h2>
              <p className="text-muted-foreground mb-6">Indstil konkurrencedygtige priser for din parkeringsplads.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="base_price_day"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pris pr. dag (kr) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="numeric"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        required
                      />
                    </FormControl>
                    <FormDescription>Grundpris for heldagsudlejning</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="base_price_hour"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pris pr. time (kr)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="numeric"
                        {...field}
                        onChange={(e) => {
                          const v = e.target.value;
                          field.onChange(v === "" ? undefined : parseFloat(v));
                        }}
                      />
                    </FormControl>
                    <FormDescription>Valgfrit: pris for timelejer</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="cancellation_policy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Afbestillingsspolitik *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Vælg politik" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="flex">Fleksibel (24t)</SelectItem>
                      <SelectItem value="mod">Moderat (48t)</SelectItem>
                      <SelectItem value="strict">Streng (72t)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Card className="bg-muted/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Prisberegning eksempel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {(() => {
                  const twoDays = (form.watch("base_price_day") || 0) * 2;
                  const hostReceives = Math.round(twoDays * 0.85);
                  return (
                    <>
                <div className="flex justify-between">
                  <span>Grundpris (2 dage):</span>
                  <span>{twoDays} kr</span>
                </div>
                <Separator />
                <div className="flex justify-between font-medium">
                  <span>Lejer betaler i alt:</span>
                  <span>{twoDays} kr</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span>Du modtager:</span>
                  <span>{hostReceives} kr</span>
                </div>
                <p className="text-xs text-muted-foreground">SharePark beholder 15% platform fee og dækker betalingsomkostninger.</p>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-4">Faciliteter</h2>
              <p className="text-muted-foreground mb-6">Hvilke faciliteter tilbyder din parkeringsplads?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="facilities.covered"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 mr-2 text-primary" />
                        <FormLabel className="text-sm font-normal">Overdækket parkering</FormLabel>
                      </div>
                      <FormDescription>Beskyttelse mod vejr og elementer</FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="facilities.gate"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <div className="flex items-center">
                        <Lock className="h-4 w-4 mr-2 text-primary" />
                        <FormLabel className="text-sm font-normal">Låge/port adgang</FormLabel>
                      </div>
                      <FormDescription>Ekstra sikkerhed med lukket adgang</FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="facilities.cctv"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <div className="flex items-center">
                        <Eye className="h-4 w-4 mr-2 text-primary" />
                        <FormLabel className="text-sm font-normal">Overvågning (CCTV)</FormLabel>
                      </div>
                      <FormDescription>Videoovervågning for ekstra sikkerhed</FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="facilities.charger"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <div className="flex items-center">
                        <Zap className="h-4 w-4 mr-2 text-success" />
                        <FormLabel className="text-sm font-normal">El-lader tilgængelig</FormLabel>
                      </div>
                      <FormDescription>Opladning til elbiler</FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            {form.watch("facilities.charger") && (
              <div className="space-y-4 p-4 rounded-lg bg-muted/30">
                <h3 className="font-medium">El-lader detaljer</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="facilities.charger_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lader type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Vælg type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Type 1">Type 1</SelectItem>
                            <SelectItem value="Type 2">Type 2</SelectItem>
                            <SelectItem value="CCS">CCS (Combo)</SelectItem>
                            <SelectItem value="CHAdeMO">CHAdeMO</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="facilities.charger_power"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Effekt</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Vælg effekt" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="3.7 kW">3.7 kW</SelectItem>
                            <SelectItem value="7.4 kW">7.4 kW</SelectItem>
                            <SelectItem value="11 kW">11 kW</SelectItem>
                            <SelectItem value="22 kW">22 kW</SelectItem>
                            <SelectItem value="50 kW+">50 kW+ (Hurtigladning)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-2">Tilgængelighed</h2>
              <p className="text-muted-foreground">
                Tilføj de tidspunkter hvor pladsen kan bookes. Du kan altid ændre tiderne fra dashboardet senere.
              </p>
            </div>
            <AvailabilityEditor value={availability} onChange={setAvailability} />
            {availability.length === 0 && (
              <div className="rounded-md border border-warning/30 bg-warning/10 p-4 text-sm text-foreground">
                Du kan fortsætte uden tider, men pladsen vises først effektivt i søgningen, når du har tilføjet fremtidig tilgængelighed.
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6 relative">
            <div>
              <h2 className="text-xl font-semibold mb-4">Billeder</h2>
              <p className="text-muted-foreground mb-6">
                Upload mindst 3 billeder af din parkeringsplads. Det første bliver hovedbilledet.
              </p>
            </div>

            <div className="space-y-4">
              <div className="relative border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                <Upload className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
                <div className="space-y-2">
                  <p className="text-sm font-medium">Klik for at uploade billeder</p>
                  <p className="text-xs text-muted-foreground">PNG, JPG op til 10MB hver. Maksimalt 10 billeder.</p>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>

              {uploadedImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {uploadedImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Upload ${index + 1}`}
                        className="w-full aspect-square object-cover rounded-lg"
                      />
                      {index === 0 && <Badge className="absolute top-2 left-2">Hovedbillede</Badge>}
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeImage(index)}
                        aria-label="Fjern billede"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {uploadedImages.length < 3 && (
                <div className="text-sm text-warning">Du skal uploade mindst 3 billeder for at fortsætte.</div>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-4">Gennemgå din plads</h2>
              <p className="text-muted-foreground mb-6">Tjek alle oplysninger før du publicerer din plads.</p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{form.watch("title") || "(Ingen titel endnu)"}</CardTitle>
                <CardDescription>{form.watch("address")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Størrelse:</span>
<span className="ml-2 font-medium">{SIZE_LABELS[form.watch("size")]}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pris/dag:</span>
                    <span className="ml-2 font-medium">{form.watch("base_price_day")} kr</span>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-2">Faciliteter</h4>
                  <div className="flex flex-wrap gap-2">
                    {form.watch("facilities.covered") && <Badge variant="secondary">Overdækket</Badge>}
                    {form.watch("facilities.charger") && <Badge variant="secondary">El-lader</Badge>}
                    {form.watch("facilities.gate") && <Badge variant="secondary">Låge/port</Badge>}
                    {form.watch("facilities.cctv") && <Badge variant="secondary">Overvågning</Badge>}
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-2">Beskrivelse</h4>
                  <p className="text-sm text-foreground">{form.watch("description")}</p>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Billeder ({uploadedImages.length})</h4>
                  <div className="flex gap-2 overflow-x-auto">
                    {uploadedImages.slice(0, 5).map((image, index) => (
                      <img
                        key={index}
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index + 1}`}
                        className="w-16 h-16 object-cover rounded flex-shrink-0"
                      />
                    ))}
                    {uploadedImages.length > 5 && (
                      <div className="w-16 h-16 bg-muted rounded flex items-center justify-center text-xs">
                        +{uploadedImages.length - 5}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="rounded-lg border bg-muted/30 p-4">
              <h4 className="font-medium mb-2">Tilgængelighed</h4>
              <p className="text-sm text-muted-foreground">
                {availability.length > 0
                  ? `${availability.length} ledighedsvindue${availability.length > 1 ? "r" : ""} tilføjet.`
                  : "Ingen tider tilføjet endnu. Pladsen kan oprettes, men bør have tider før den markedsføres."}
              </p>
            </div>

            <FormField
              control={form.control}
              name="visibility"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal">Publicér pladsen med det samme</FormLabel>
                    <FormDescription>
                      Hvis ikke markeret, kan du aktivere pladsen senere fra dit dashboard
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const isSubmitting = createListingMutation.isPending;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-6 max-w-5xl">
        <Button variant="ghost" className="mb-6" onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Tilbage til dashboard
        </Button>

        <div className="mb-8 rounded-xl border bg-card p-4 sm:p-6 shadow-sm">
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">Opret ny parkeringsplads</h1>
              <p className="text-sm text-muted-foreground">Guidet opsætning til adresse, pris, billeder og bookbare tider.</p>
            </div>
            <Badge variant="secondary" className="w-fit">Trin {currentStep} af {STEPS.length}</Badge>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <Progress value={progress} className="flex-1" />
          </div>

          {/* Step indicator */}
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;

              return (
                <div
                  key={step.id}
                  className={`flex min-h-12 items-center gap-2 rounded-md border px-2 py-2 text-xs ${
                    isActive ? "border-primary bg-primary/10 text-primary" : isCompleted ? "border-success/30 bg-success/10 text-success" : "bg-background text-muted-foreground"
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <Icon className="h-4 w-4 shrink-0" />}
                  <span className="leading-tight">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>

        <Form {...form}>
  <form
  noValidate
  onSubmit={(e) => {
    e.preventDefault();
    form.handleSubmit(onSubmit)(e as any);
  }}
  onKeyDown={(e) => {
    if (e.key === "Enter" && currentStep < STEPS.length) {
      e.preventDefault(); // blokér enter-submit før sidste trin
    }
  }}
  className="space-y-8"
>
    <Card className="shadow-sm">
      <CardContent className="p-4 sm:p-8">{renderStep()}</CardContent>
  </Card>

  <div className="sticky bottom-0 z-30 -mx-4 flex justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0">
    <Button
      type="button"
      variant="outline"
      onClick={prevStep}
      disabled={currentStep === 1 || isSubmitting}
    >
      <ArrowLeft className="h-4 w-4 mr-2" />
      Forrige
    </Button>

    {currentStep < STEPS.length ? (
      <Button
        key="next-btn"
        type="button"
        onClick={(e) => {
          e.preventDefault();       // sikrer at click ikke submitter
          void nextStep();
        }}
        disabled={(currentStep === 5 && uploadedImages.length < 3) || isSubmitting}
      >
        Næste
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    ) : (
      <Button
        key="submit-btn"
        type="submit"
        disabled={isSubmitting || uploadedImages.length < 3}
      >
        {isSubmitting ? "Opretter..." : "Opret parkeringsplads"}
      </Button>
    )}
  </div>
</form>

        </Form>
      </div>
      
      <TermsAcceptanceDialog
        open={showTermsDialog}
        onOpenChange={setShowTermsDialog}
        requiredType="host"
        onAccepted={async () => {
          if (pendingSubmitData) {
            try {
              await createListingMutation.mutateAsync(pendingSubmitData);
            } finally {
              setPendingSubmitData(null);
            }
          }
        }}
      />
    </div>
  );
}
